import { isEqual } from 'lodash';
import { memo, useMemo } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { SliderWithTitle } from '@hivebrite/design-system/Slider';
import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { UserPresentationCard } from '@hivebrite/design-system/UserPresentationCard';

import type {
  QueryAttributes,
  SliderItemAttributes,
} from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/types';
import { SLIDER_TYPES } from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/types';
import type { LaneWrapperUser } from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/UsersLaneWrapper/types';
import { perPageBreakpoints } from 'hb-builder/components/BuilderComponents/dynamic/Slider/utils/breakpoints';
import BuilderThemeProvider, {
  CONTAINER_TYPE,
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import UserLaneCardLoader from 'hb-builder/components/common/LoadingComponents/UserLaneCardLoader';
import type { AuthenticationIds } from 'hb-builder/types/config/AuthenticationIds';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import { APP_TYPE } from 'hb-react/types/appType';

import type { FormattedUser } from './mapData';
import { mapUsers } from './mapData';
import { getRedirectionUrl } from './utils/redirection';

type SliderMemoizedProps = {
  items: FormattedUser[];
  link: {
    text: string;
    url: string;
    target: string;
  };
  numberOfItems: QueryAttributes['numberOfItems'];
  title: string;
} & Pick<UserLaneProps, 'data' | 'isLoading'>;

const SliderMemoized = memo(
  ({ data: users, items, isLoading, link, title }: SliderMemoizedProps) => (
    <SliderWithTitle
      title={title}
      link={link}
      perPageBreakpoints={perPageBreakpoints[SLIDER_TYPES.USERS]}
      items={items}
      ItemComponent={(item) => (
        <BuilderThemeProvider
          containerType={CONTAINER_TYPE.CARD}
          key={item.id}
          viewAs={VIEW_AS.USER}
        >
          <StyledBox>
            <UserPresentationCard {...item} />
          </StyledBox>
        </BuilderThemeProvider>
      )}
      total={users.length}
      loading={isLoading}
      LoadingItemComponent={UserLaneCardLoader}
    />
  ),
  (prevProps, nextProps) => {
    const sameTitle = prevProps.title === nextProps.title;
    const sameItems = isEqual(prevProps.items, nextProps.items);
    const sameNumberOfItems = isEqual(
      prevProps.numberOfItems,
      nextProps.numberOfItems,
    );

    return sameTitle && sameItems && sameNumberOfItems;
  },
);

interface UserLaneProps {
  app: APP_TYPE;
  data: LaneWrapperUser[];
  isLoading: boolean;
  item: DocumentItem<SliderItemAttributes>;
  ids: AuthenticationIds;
}

const UsersLane = ({
  app,
  data: users,
  isLoading,
  item,
  ids,
}: UserLaneProps) => {
  const { dynamicAttributes } = item.attributes || {};
  const breakpoint = useBreakpoint();

  const allItems = useMemo(
    () => mapUsers({ app, users, dynamicAttributes }),
    [app, dynamicAttributes, users],
  );

  if (!users || !dynamicAttributes || !allItems) {
    return null;
  }

  const {
    title,
    query: { dataUserType },
  } = dynamicAttributes;
  const linkText =
    breakpoint !== 's'
      ? s__('Builder|See all members')
      : s__('Builder|See all');
  const sliderLink =
    app === APP_TYPE.BACKOFFICE
      ? { text: linkText, url: '#', target: '' }
      : {
          text: linkText,
          url: getRedirectionUrl(ids, dataUserType),
          target: '_self',
        };

  return (
    <Wrapper flexDirection="column">
      <SliderMemoized
        data={users}
        isLoading={isLoading}
        items={allItems}
        link={sliderLink}
        title={title}
        numberOfItems={dynamicAttributes.query.numberOfItems}
      />
    </Wrapper>
  );
};

export default UsersLane;

const Wrapper = styled(Box).attrs(() => ({
  py: 4,
}))``;

const StyledBox = styled(Box).attrs(() => ({
  display: 'block',
  height: '100%',
  py: 1,
}))`
  * {
    text-decoration: none !important;
  }
`;
